import React, { FC, useState } from 'react';
import { useAppSelector } from '@hooks/store';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import { NavigationItem } from '@components/index';
import {
  homeUrl,
  adviserUrl,
  profileUrl,
  documentsUrl,
  beneficiariesUrl,
  insuranceUrl,
  pensionUrl,
  benefitStatementUrl,
  contributionsUrl,
  logoutUrl,
} from '@utils/UrlBuilder';
import { NavigationItemType } from '@src/app/types';
import { SupersimplifierStore, selectSupersimplifier } from '@store/reducers/supersimplifier';
import './_header.scss';
import strings from '@strings/LocalisedStrings';
import { Routes } from '@enums/index';
import { MoreVert } from '@mui/icons-material';
import { useAppContext } from '@src/app/hooks/useAppContext';
import routes from '@src/route.config';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { useMediaQuery, useTheme } from '@mui/material';
import Colors from '@src/app/config/colorConfig';

const Header: FC = (): JSX.Element => {
  const superSimplifierStore: SupersimplifierStore = useAppSelector(selectSupersimplifier);
  const { logoUrl } = useAppSelector(selectPortfolios);
  const { drawerExpanded, setDrawerExpanded, setTransitioningMenu } = useAppContext();
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  const theme = useTheme();
  const location = useLocation();

  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isOnboarding = location.pathname.includes('onboarding');

  const subMenu: NavigationItemType[] | boolean = [
    { name: strings.profile, key: Routes.Profile, route: profileUrl() },
    { name: strings.logout, key: Routes.Logout, route: logoutUrl() },
  ];

  const subMenuSuper: NavigationItemType[] | boolean = [];
  let isPensionAccount = false;
  let isSuperAccount = false;

  if (superSimplifierStore.superSimplifierAccounts?.data?.length) {
    if (superSimplifierStore.superSimplifierAccounts.data[0].accountNumber && superSimplifierStore.superSimplifierAccounts.data[0].name) {
      isPensionAccount = !!superSimplifierStore.superSimplifierAccounts.data.find((i) => i.type === 'Pension') || false;
      isSuperAccount = !!superSimplifierStore.superSimplifierAccounts.data.find((i) => i.type === 'Super') || false;
    }
    subMenuSuper.push({
      name: 'Benefit Statement',
      key: Routes.BenefitStatement,
      route: benefitStatementUrl(),
    });
    subMenuSuper.push({
      name: 'Beneficiaries',
      key: Routes.Beneficiaries,
      route: beneficiariesUrl(),
    });
    if (isPensionAccount) {
      subMenuSuper.push({ name: 'Pension Details', key: Routes.Pension, route: pensionUrl() });
    }
    subMenuSuper.push({ name: 'Insurance', key: Routes.Insurance, route: insuranceUrl() });
    if (isSuperAccount) {
      subMenuSuper.push({
        name: 'Contributions',
        key: Routes.Contributions,
        route: contributionsUrl(),
      });
    }
    subMenuSuper.push({ name: 'Documents', key: Routes.Documents, route: documentsUrl() });
  }

  const getNavigationItems = (isMobile: boolean) => {
    const navigationItems: NavigationItemType[] = [];

    navigationItems.push({
      name: strings.adviser,
      key: Routes.Adviser,
      leftIcon: (
        <PersonOutlineOutlinedIcon
          fontSize="large"
          sx={{ marginBottom: 0, paddingBottom: 0, color: isMobile ? theme.palette.common.black : theme.palette.common.white }}
        />
      ),
      route: adviserUrl(),
    });

    navigationItems.push({
      name: strings.profile,
      key: Routes.Profile,
      leftIcon: (
        <SettingsOutlinedIcon
          fontSize="large"
          sx={{ marginBottom: 0, paddingBottom: 0, color: isMobile ? theme.palette.common.black : theme.palette.common.white }}
        />
      ),
      rightIcon: (
        <ExpandMoreOutlinedIcon
          fontSize="large"
          sx={{ marginBottom: 0, paddingBottom: 0, color: isMobile ? theme.palette.common.black : theme.palette.common.white }}
        />
      ),
      menu: subMenu,
    });
    return navigationItems;
  };

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = (page?: NavigationItemType, close = false) => {
    // don't close menu if there's a submenu (only close when close = true)
    if (page && page.menu && !close) return;
    setAnchorElNav(null);
  };

  const handleLoginPressed = async () => {
    await loginWithRedirect();
  };

  const LoginButton: FC<{ inverted?: boolean }> = (): JSX.Element => (
    <Button
      sx={{
        backgroundColor: Colors.white,
        color: theme.palette.primary.main,
        ':hover': {
          backgroundColor: Colors.white,
          color: theme.palette.primary.main,
        },
      }}
      variant="contained"
      onClick={handleLoginPressed}
    >
      {strings.login}
    </Button>
  );

  const onProtectedRoute = routes.find((x) => x.path === location.pathname && x.protected) !== null;

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        backgroundColor: theme.palette.secondary.main,
        zIndex: isMd ? theme.zIndex.appBar - 10 : theme.zIndex.drawer + 10,
      }}
    >
      <Container maxWidth={onProtectedRoute && isAuthenticated ? false : 'lg'}>
        <Toolbar disableGutters sx={{ paddingTop: 2, paddingBottom: 2 }}>
          <Box gap={2} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
            {onProtectedRoute && isAuthenticated && logoUrl && (
              <Link to={homeUrl()} color="inherit" style={{ display: 'block', width: '100%', height: '100%' }}>
                <Box
                  component="img"
                  src={logoUrl}
                  alt="Logo"
                  sx={{
                    height: '100%',
                    maxHeight: '65px',
                    width: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Link>
            )}

            {onProtectedRoute && isAuthenticated && (
              <IconButton
                size="large"
                aria-label="toggle the sidenav menu"
                onClick={() => {
                  setDrawerExpanded(!drawerExpanded);
                  setTransitioningMenu(true);
                }}
                sx={{ color: Colors.white, flexGrow: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>

          <Box sx={{ display: { md: 'flex', xs: 'none' } }} flex={1} />

          {!isOnboarding && (
            <Box width="100%" justifyContent="space-between" sx={{ display: { xs: 'flex', md: 'none' } }}>
              {onProtectedRoute && isAuthenticated && (
                <IconButton
                  size="large"
                  aria-label="toggle the sidenav menu"
                  onClick={() => setDrawerExpanded(!drawerExpanded)}
                  sx={{ color: Colors.white }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              {isAuthenticated && (
                <IconButton size="large" aria-label="account of current user" onClick={handleOpenNavMenu} sx={{ color: Colors.white }}>
                  <MoreVert />
                </IconButton>
              )}
              {!isAuthenticated && (
                <>
                  <Box flex={1} />
                  <LoginButton />
                </>
              )}
              {!isLoading && (
                <Menu
                  id="menuAppbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={() => handleCloseNavMenu()}
                  sx={{
                    display: { xs: 'block', md: 'none' },
                  }}
                >
                  {isAuthenticated &&
                    getNavigationItems(true).map((page: NavigationItemType) => (
                      <MenuItem key={page.name} className="mobileNavMenu" onClick={() => handleCloseNavMenu(page)}>
                        <NavigationItem
                          isMobile
                          key={page.name}
                          navigation={page}
                          handleCloseParentNavMenu={page.menu ? handleCloseNavMenu : undefined}
                        />
                      </MenuItem>
                    ))}
                </Menu>
              )}
            </Box>
          )}
          {!isLoading && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
              }}
            >
              {isAuthenticated
                ? getNavigationItems(false).map((page) => <NavigationItem key={page.name} navigation={page} />)
                : !isOnboarding && <LoginButton />}
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
